// src/services/reportsCache.js
//
// In-memory cache for the portfolio Reports client. Mirror of
// services/locationCache.js (same TTL, same shape, same invalidate-all
// rule) but kept as a separate store so the Reports cache can be
// reset independently in tests and so each module owns its own
// keys cleanly.
//
// The "full bust on every write" rule is enforced by services/cacheBus.js,
// which both locationsClient and leasesClient call after a successful
// write. That keeps the three GET /api/reports/* aggregates consistent
// with location/lease state without per-key invalidation logic on the
// client.
//
// Cache keys:
//   "lease-countdown"     -> Array<LeaseCountdownRow>
//   "office-capacity"     -> Array<OfficeCapacityRow>
//   "office-utilization"  -> Array<OfficeUtilizationRow>

const TTL_MS = 5 * 60 * 1000; // 5 minutes — matches locationCache

/** @type {Map<string, { value: any, expiresAt: number }>} */
const store = new Map();

export function get(key) {
  const entry = store.get(key);
  if (!entry) return undefined;
  if (Date.now() >= entry.expiresAt) {
    store.delete(key);
    return undefined;
  }
  return entry.value;
}

export function set(key, value) {
  store.set(key, { value, expiresAt: Date.now() + TTL_MS });
}

export function invalidateAll() {
  store.clear();
}

export function __resetForTests() {
  store.clear();
}

export function __sizeForTests() {
  return store.size;
}

export const KEY_LEASE_COUNTDOWN    = "lease-countdown";
export const KEY_OFFICE_CAPACITY    = "office-capacity:v2";
export const KEY_OFFICE_UTILIZATION = "office-utilization";
// Slice UTIL-S3 — varies by month param; prefix + keyFor() pattern.
export const KEY_PREFIX_OFFICE_UTILIZATION_PORTFOLIO = "office-utilization-portfolio";
export const KEY_PREFIX_UTILIZATION = "utilization:v2";
// REPORTS-DATA-COVERAGE-S1 — data coverage report. No querystring filters; flat key.
// HEADCOUNT-TREND-REPORT — per-import headcount trend. No querystring filters; flat key.
export const KEY_HEADCOUNT_TREND = "headcount-trend";
export const KEY_PREFIX_RTO_MONTHLY = "rto-compliance-monthly:v1";
export const KEY_PREFIX_RTO_QUARTERLY = "rto-compliance-quarterly:v1";
export const KEY_PREFIX_RTO_WEEKLY = "rto-compliance-weekly:v1";
export const KEY_PREFIX_FOOT_TRAFFIC = "foot-traffic:v1";
export const KEY_COST_LEAKAGE = "cost-leakage:v2";
export const TTL_MS_FOR_TESTS       = TTL_MS;

/**
 * Build a deterministic cache key from a prefix + small param object.
 * Keys are sorted, undefined/null/empty params are dropped, values coerced
 * to strings. Mirrors the server-side keyFor() in portfolioReportsCache.js.
 */
export function keyFor(prefix, params = {}) {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${k}=${String(v)}`);
  return parts.length === 0 ? prefix : `${prefix}?${parts.join("&")}`;
}
